import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { AGENT_LABELS } from '../mock';
import { relTime, pct } from '../lib/data';
import { useAgents, useAgentEvents } from '../api/hooks';
import PageHeader from '../components/PageHeader';
import Stat from '../components/Stat';
import StatusPill from '../components/StatusPill';
import Confidence from '../components/Confidence';
import { Button } from '../components/ui/button';
import {
  Bot, Zap, AlertTriangle, Clock, Send, CheckCircle2, Activity, ChevronRight, DollarSign,
} from 'lucide-react';

const usd = (n) => `$${(n || 0).toFixed(2)}`;
const usd4 = (n) => `$${(n || 0).toFixed(4)}`;

const OUTCOME = {
  auto_cleared: { tone: 'green', icon: Zap, label: 'Auto-cleared' },
  flagged: { tone: 'amber', icon: AlertTriangle, label: 'Flagged' },
  awaiting_review: { tone: 'amber', icon: Clock, label: 'Routed to review' },
  sent: { tone: 'sky', icon: Send, label: 'Sent' },
  applied: { tone: 'green', icon: CheckCircle2, label: 'Applied' },
};

export default function AgentActivity() {
  const { session } = useAuth();
  const practiceId = session?.practice?.id || 'prac_001';
  const [agentFilter, setAgentFilter] = useState('all');

  const { data: agents = [] } = useAgents(practiceId);
  const { data: allEvents = [] } = useAgentEvents(practiceId);

  const events = useMemo(
    () => allEvents.filter((e) => agentFilter === 'all' || e.agent === agentFilter),
    [allEvents, agentFilter]
  );

  const totalRuns = agents.reduce((s, a) => s + a.runsToday, 0);
  const autoCleared = allEvents.filter((e) => e.outcome === 'auto_cleared').length;
  const routed = allEvents.filter((e) => e.outcome === 'awaiting_review').length;
  // What it cost us to run these agents today — the "frequency cost".
  const costToday = allEvents.reduce((s, e) => s + (e.costUsd || 0), 0);
  const tokensToday = allEvents.reduce((s, e) => s + (e.tokensInput || 0) + (e.tokensOutput || 0), 0);

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Transparency · HIPAA audit"
        title="Agent activity"
        subtitle="Everything your agents did, what ran on autopilot, and what they handed to a human."
      >
        <Link to="/app/settings"><Button variant="outline"><Zap className="h-4 w-4 mr-1.5" />Automation rules</Button></Link>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label="Agent runs today" value={totalRuns} sub="across 7 agents" icon={Activity} tone="indigo" />
        <Stat label="Auto-handled" value={`${Math.round((autoCleared / (autoCleared + routed || 1)) * 100)}%`} sub="no human needed" icon={Zap} tone="emerald" />
        <Stat label="Routed to humans" value={routed} sub="exceptions only" icon={AlertTriangle} tone="amber" />
        <Stat label="Spent today" value={usd(costToday)} sub={`${(tokensToday / 1000).toFixed(1)}k tokens`} icon={DollarSign} tone="violet" />
        <Stat label="Avg confidence" value="89%" sub="parse accuracy" icon={Bot} tone="violet" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Agents list */}
        <div className="space-y-3">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Agents</div>
          <button onClick={() => setAgentFilter('all')} className={`w-full text-left dh-card p-3 lift ${agentFilter === 'all' ? 'ring-2 ring-brand-200' : ''}`}>
            <div className="text-sm font-medium text-slate-900">All agents</div>
            <div className="text-xs text-slate-500">{allEvents.length} events today</div>
          </button>
          {agents.map((a) => (
            <button key={a.key} onClick={() => setAgentFilter(a.key)} className={`w-full text-left dh-card p-3 lift ${agentFilter === a.key ? 'ring-2 ring-brand-200' : ''}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="h-7 w-7 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center shrink-0"><Bot className="h-4 w-4" /></div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{a.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{a.description}</div>
                  </div>
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className={`pill ${a.mode === 'autopilot' ? 'pill-green' : 'pill-indigo'}`}>{a.mode === 'autopilot' ? 'Autopilot' : 'Copilot'}</span>
                <span className="text-[11px] text-slate-500 tnum">{a.runsToday} runs · {pct(a.autoRate)} auto</span>
              </div>
            </button>
          ))}
        </div>

        {/* Activity feed */}
        <div className="lg:col-span-2">
          <div className="dh-card-flush">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-900">{agentFilter === 'all' ? 'All activity' : `${AGENT_LABELS[agentFilter]} Agent`}</div>
              <div className="flex items-center gap-1.5 text-[11px] text-emerald-600"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 dh-pulse" />live</div>
            </div>
            <ul className="divide-y divide-slate-100">
              {events.map((e) => {
                const o = OUTCOME[e.outcome] || OUTCOME.flagged;
                const Icon = o.icon;
                const href = e.refType === 'verification' ? `/app/eligibility/${e.refId}` : '/app';
                return (
                  <li key={e.id}>
                    <Link to={href} className="row-hover flex items-start gap-3 px-5 py-3.5 group">
                      <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 bg-violet-50 text-violet-600`}><Bot className="h-4 w-4" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-medium text-violet-700">{AGENT_LABELS[e.agent]}</span>
                          <span className={`pill pill-${o.tone === 'green' ? 'green' : o.tone === 'amber' ? 'amber' : 'sky'}`}><Icon className="h-3 w-3" />{o.label}</span>
                        </div>
                        <div className="text-sm text-slate-800 mt-0.5">{e.title}</div>
                        <div className="text-xs text-slate-500">{e.detail}</div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span className="text-[11px] text-slate-400 whitespace-nowrap">{relTime(e.ts)}</span>
                        <Confidence value={e.confidence} showLabel={false} />
                        {e.costUsd != null && (
                          <span className="text-[10px] text-slate-400 tnum whitespace-nowrap" title={`${((e.tokensInput || 0) + (e.tokensOutput || 0)).toLocaleString()} tokens · ${e.model || ''}`}>
                            {usd4(e.costUsd)} · {(((e.tokensInput || 0) + (e.tokensOutput || 0)) / 1000).toFixed(1)}k tok
                          </span>
                        )}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
